// src/api/authHelpers.js
//
// Shared Supabase client + JWT-token helpers used by:
//   - frontend/src/index.js  (default axios + window.fetch interceptors)
//   - frontend/src/api/client.js  (shared `api` axios.create instance)
//
// Single supabase client instance prevents duplicate auth state across the
// app. `setAxiosAuthDefault` mirror is kept for now (Phase 3 cleanup deferred
// until 11 axios.create callsites are migrated to import { api }).
//
// Origin: 2026-05-06 — Samuel hit 401 storm on /products/<sku> after PR #379's
// defaults-header mirror failed to cover the startup race. See
// docs/ideas/2026-05-06-axios-shared-instance.md and
// docs/knowledge/architecture/auth-gate-pattern.md.

import axios from 'axios';
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  persistSession: true,
  autoRefreshToken: true,
});

// Refresh 5 minutes before expiry — was 60 s, which raced expiry on slow
// networks and burned the cached token. 300 s gives plenty of headroom and
// drastically reduces the chance of a 401 cascade. Origin: 2026-05-06
// "logout after 1 hour" report.
export const SESSION_REFRESH_LEEWAY_SEC = 300;

let _inFlightRefresh = null;

// Distinguish a real auth failure (refresh token invalid/expired) from a
// transient network error. Only the first should kick the user back to login;
// transient errors retry silently on the next poll cycle.
const isFatalAuthError = (err) => {
  if (!err) return false;
  if (err.status === 400 || err.status === 401) return true;
  const msg = String(err.message || err.error_description || '').toLowerCase();
  return (
    msg.includes('invalid_grant') ||
    msg.includes('invalid refresh') ||
    msg.includes('refresh_token_not_found')
  );
};

export const setAxiosAuthDefault = (token) => {
  if (!axios?.defaults?.headers) return;
  if (!axios.defaults.headers.common) axios.defaults.headers.common = {};
  if (token) {
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common.Authorization;
  }
};

export const dispatchSessionLost = () => {
  try {
    window.dispatchEvent(new CustomEvent('pog:session-lost'));
  } catch { /* old browsers — fall through */ }
};

export const refreshSessionOnce = () => {
  // Single-flight: collapse parallel refresh requests into one network call.
  // Without this, a burst of 6 API calls would issue 6 refresh-token POSTs
  // and Supabase rotates the refresh token on each — losing the race.
  if (_inFlightRefresh) return _inFlightRefresh;

  const doRefresh = async () => {
    // Re-check the cached session under the lock — another tab may have
    // just refreshed it, in which case we don't need to call the API.
    try {
      const { data: cached } = await supabase.auth.getSession();
      const session = cached?.session;
      if (session?.expires_at) {
        const now = Math.floor(Date.now() / 1000);
        if (session.expires_at - now > SESSION_REFRESH_LEEWAY_SEC) {
          setAxiosAuthDefault(session.access_token);
          return session;
        }
      }
    } catch { /* fall through to actual refresh */ }

    const { data, error } = await supabase.auth.refreshSession();
    if (error || !data?.session) {
      const err = error || new Error('No session after refresh');
      if (isFatalAuthError(err)) {
        setAxiosAuthDefault(null);
        dispatchSessionLost();
      }
      // Transient: do NOT dispatch. Caller will retry later.
      return null;
    }
    setAxiosAuthDefault(data.session.access_token);
    return data.session;
  };

  _inFlightRefresh = (async () => {
    try {
      // Cross-tab coordination: Web Locks API ensures only one tab refreshes
      // at a time. Other tabs wait for the lock and then re-read the cached
      // session (already updated via Supabase storage-event sync). Falls back
      // to a plain refresh on browsers without navigator.locks (Safari <17).
      if (typeof navigator !== 'undefined' && navigator.locks?.request) {
        return await navigator.locks.request(
          'pog-supabase-refresh',
          { mode: 'exclusive' },
          doRefresh,
        );
      }
      return await doRefresh();
    } catch (e) {
      if (isFatalAuthError(e)) {
        setAxiosAuthDefault(null);
        dispatchSessionLost();
      }
      return null;
    } finally {
      _inFlightRefresh = null;
    }
  })();
  return _inFlightRefresh;
};

export const getFreshAccessToken = async () => {
  const { data } = await supabase.auth.getSession();
  const session = data?.session;
  if (!session) {
    setAxiosAuthDefault(null);
    return null;
  }
  const expiresAt = session.expires_at || 0;
  const now = Math.floor(Date.now() / 1000);
  if (expiresAt - now <= SESSION_REFRESH_LEEWAY_SEC) {
    const refreshed = await refreshSessionOnce();
    return refreshed?.access_token || null;
  }
  setAxiosAuthDefault(session.access_token);
  return session.access_token;
};

export const isPublicUrl = (url) => url.includes('/vendor-reports/public/');

export const isCrossOriginUrl = (url) => {
  if (!/^https?:\/\//i.test(url)) return false;
  if (url.startsWith(window.location.origin)) return false;
  const apiBase = process.env.REACT_APP_API_BASE_URL || '';
  if (apiBase && url.startsWith(apiBase)) return false;
  return true;
};
